/*
 * Super Admin → Authentication Health Check.
 * Runs live, non-destructive checks (hashing, sessions, invalidation, lockout,
 * temp-password expiry, unified login route) and shows PASS/FAIL + last tested.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { Loader2, ShieldCheck, CheckCircle2, XCircle, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AuthHealthCheckPage({ session }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const headers = {
    "x-user-role": session?.user?.role || "admin",
    "x-user-id": session?.user?.id || "",
  };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/auth/health-check`, { headers });
      setData(r.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const run = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/api/admin/auth/health-check/run`, {}, { headers });
      setData(r.data);
      toast[r.data.overall === "PASS" ? "success" : "error"](`Authentication ${r.data.overall}`);
    } catch { toast.error("Health check failed to run"); } finally { setRunning(false); }
  };

  const overall = data?.overall;

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8" data-testid="auth-health-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[#0b1220]">
            <ShieldCheck className="h-6 w-6 text-[#007a9e]" />
            <h1 className="text-2xl font-bold">Authentication Health Check</h1>
          </div>
          <p className="mt-1 text-sm text-[#6b7280]">Prove login, logout, reset and redirect are green before a demo.</p>
        </div>
        <Button onClick={run} disabled={running} className="bg-[#0b1220] hover:bg-black text-white" data-testid="auth-health-run">
          {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Run check
        </Button>
      </div>

      {loading ? (
        <div className="mt-10 text-center text-[#6b7280]"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
      ) : (
        <div className="mt-6">
          <div
            className={`rounded-xl border p-5 flex items-center justify-between ${overall === "PASS" ? "border-emerald-200 bg-emerald-50" : overall === "FAIL" ? "border-red-200 bg-red-50" : "border-[#e5e7eb] bg-[#f9fafb]"}`}
            data-testid="auth-health-overall"
          >
            <div>
              <div className={`text-lg font-bold ${overall === "PASS" ? "text-emerald-700" : overall === "FAIL" ? "text-red-700" : "text-[#6b7280]"}`}>
                {overall ? `Authentication ${overall}` : "Not yet tested"}
              </div>
              {data?.last_tested_at && (
                <div className="text-xs text-[#6b7280] mt-0.5">
                  Last tested {new Date(data.last_tested_at).toLocaleString()}
                  {typeof data.passed === "number" && ` · ${data.passed}/${data.total} checks passed`}
                </div>
              )}
            </div>
            {overall === "PASS" ? <CheckCircle2 className="h-8 w-8 text-emerald-600" /> : overall === "FAIL" ? <XCircle className="h-8 w-8 text-red-600" /> : null}
          </div>

          <div className="mt-4 space-y-2" data-testid="auth-health-checks">
            {(data?.checks || []).map((c, i) => (
              <div key={i} className="flex items-start gap-3 rounded-lg border border-[#e5e7eb] bg-white px-4 py-3" data-testid={`auth-health-check-${i}`}>
                {c.status === "PASS"
                  ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                  : <XCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />}
                <div className="flex-1">
                  <div className="text-[14px] font-medium text-[#111827]">{c.name}</div>
                  <div className="text-[12px] text-[#6b7280]">{c.detail}</div>
                </div>
                <span className={`text-[11.5px] font-bold ${c.status === "PASS" ? "text-emerald-600" : "text-red-600"}`}>{c.status}</span>
              </div>
            ))}
            {(!data?.checks || data.checks.length === 0) && (
              <div className="text-center text-sm text-[#6b7280] py-8">Click "Run check" to test the authentication system.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
